import { Landmark } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAlertDialog } from "@/shared/components/feedback/ColocaAlertDialog";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { TAX_COUNTRIES } from "../../constants/paymentTaxes.constants";
import type { UsePaymentTaxesResult } from "../../hooks/usePaymentTaxes";
import { useTaxMutations } from "../../hooks/useTaxMutations";
import type { PaymentTax, TaxScopeGroup, TaxSummary } from "../../types/paymentTaxes.types";
import { rowsCoveredBy, taxHistoryAccess } from "../../utils/taxMath";
import type { EditTaxIntent } from "./EditTaxDialog";
import { TaxCountrySection, type TaxRowActions } from "./TaxCountrySection";
import { TaxHistoryDrawer } from "./TaxHistoryDrawer";
import { scopeLabel } from "./taxScopeLabels";

type Confirm =
  | { kind: "exempt"; summary: TaxSummary; general: TaxScopeGroup }
  | { kind: "back"; summary: TaxSummary; own: TaxScopeGroup };

export interface TaxRulesTabProps {
  state: UsePaymentTaxesResult;
  scopeOrgId: string | null;
  orgName: string | undefined;
  canWrite: boolean;
  onEditTax: (intent: EditTaxIntent) => void;
}

/** Every rule of every country, with its versions: the price book's rule tabs, for taxes. */
export function TaxRulesTab({ state, scopeOrgId, orgName, canWrite, onEditTax }: TaxRulesTabProps) {
  const { t } = useTranslations("paymentTaxes");
  const mutations = useTaxMutations();
  const [history, setHistory] = useState<{
    summary: TaxSummary;
    group: TaxScopeGroup;
    inherited: boolean;
  } | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const countries = TAX_COUNTRIES.map((country) => country.iso3).filter((iso3) =>
    (state.byCountry.get(iso3) ?? []).some(
      (summary) =>
        summary.scopes.length > 0 ||
        (!!scopeOrgId && summary.organizationScopes.some((group) => group.organizationId === scopeOrgId)),
    ),
  );

  const actions: TaxRowActions = useMemo(
    () => ({
      onChange: (_summary, group) =>
        onEditTax({
          source: group.current ?? group.versions[0] ?? null,
          lockScope: true,
          organizationId: group.organizationId,
          organizationName: orgName,
          current: group.current,
        }),
      onHistory: (summary, group, inherited) => {
        mutations.clearError();
        setHistory({ summary, group, inherited });
      },
      onSpecial: (_summary, general) =>
        onEditTax({
          source: general.current ?? general.versions[0] ?? null,
          lockScope: true,
          organizationId: scopeOrgId,
          organizationName: orgName,
          current: general.current,
        }),
      onExempt: (summary, general) => {
        mutations.clearError();
        setConfirm({ kind: "exempt", summary, general });
      },
      onBackToGeneral: (summary, own) => {
        mutations.clearError();
        setConfirm({ kind: "back", summary, own });
      },
    }),
    [mutations, onEditTax, orgName, scopeOrgId],
  );

  const handleConfirm = useCallback(async () => {
    if (!confirm || !scopeOrgId) return;
    const org = orgName ?? scopeOrgId;
    if (confirm.kind === "exempt") {
      const general = confirm.general.current ?? confirm.general.versions[0];
      const created = await mutations.createTax({
        code: confirm.summary.code,
        name: confirm.summary.name,
        country: confirm.summary.country,
        base: confirm.summary.base,
        bearer: general?.bearer ?? "CLIENT",
        rate: "0",
        organization_id: scopeOrgId,
        ...(confirm.general.rail ? { rail: confirm.general.rail } : {}),
        ...(confirm.general.direction ? { direction: confirm.general.direction } : {}),
      });
      if (!created) return;
      colocaToast.success(t("toast_exempted", { org, name: confirm.summary.name }));
    } else {
      // The version in force goes last, so a failure halfway leaves it charging what it charged.
      const ids = [...confirm.own.versions]
        .sort((a, b) => Number(a.id === confirm.own.current?.id) - Number(b.id === confirm.own.current?.id))
        .map((tax) => tax.id);
      if (!(await mutations.removeTaxes(ids))) {
        state.refresh();
        return;
      }
      colocaToast.success(t("toast_back_general", { org, name: confirm.summary.name }));
    }
    setConfirm(null);
    state.refresh();
  }, [confirm, scopeOrgId, orgName, mutations, state, t]);

  const historyContext = (summary: TaxSummary, group: TaxScopeGroup, inherited: boolean) => {
    const where = scopeLabel(group, t);
    const covered = group.organizationId ? rowsCoveredBy(summary, group) : [];
    const access = taxHistoryAccess(group, scopeOrgId, inherited);
    return {
      scope: group.organizationId
        ? t("history_scope_own", { org: orgName ?? group.organizationId, where })
        : t("history_scope_general", { where }),
      covers:
        covered.length > 1
          ? t("history_scope_covers", { rows: covered.map((row) => scopeLabel(row, t)).join(", ") })
          : null,
      readOnlyReason:
        access === "editable"
          ? null
          : t(access === "general" ? "history_read_only_general" : "history_read_only_inherited"),
    };
  };

  const handleDeleteVersion = useCallback(
    async (tax: PaymentTax) => {
      const removed = await mutations.removeTaxes([tax.id]);
      if (removed) {
        colocaToast.success(t("toast_deleted"));
        setHistory(null);
        state.refresh();
      }
      return removed;
    },
    [mutations, state, t],
  );

  return (
    <>
      {state.isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((index) => (
            <ColocaSkeleton key={index} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : countries.length === 0 ? (
        <ColocaEmpty
          icon={Landmark}
          title={t(scopeOrgId ? "empty_org_title" : "empty_title")}
          description={t(scopeOrgId ? "empty_org_description" : "empty_description")}
        />
      ) : (
        <div className="space-y-6">
          {state.truncated && <ColocaAlert variant="warning" text={t("truncated_notice")} />}
          {countries.map((country) => (
            <TaxCountrySection
              key={country}
              country={country}
              taxes={state.byCountry.get(country) ?? []}
              scopeOrgId={scopeOrgId}
              now={state.now}
              canWrite={canWrite}
              actions={actions}
            />
          ))}
        </div>
      )}

      {history && (
        <TaxHistoryDrawer
          group={history.group}
          now={state.now}
          canWrite={canWrite}
          {...historyContext(history.summary, history.group, history.inherited)}
          isDeleting={mutations.isSubmitting}
          error={mutations.error}
          onDelete={handleDeleteVersion}
          onClose={() => setHistory(null)}
        />
      )}

      {confirm && (
        <ColocaAlertDialog
          open
          onOpenChange={(next) => {
            if (!next && !mutations.isSubmitting) setConfirm(null);
          }}
          title={t(confirm.kind === "exempt" ? "exempt_title" : "back_general_title", {
            name: confirm.summary.name,
          })}
          description={t(confirm.kind === "exempt" ? "exempt_description" : "back_general_description", {
            org: orgName ?? scopeOrgId ?? "",
            name: confirm.summary.name,
            where: scopeLabel(confirm.kind === "exempt" ? confirm.general : confirm.own, t),
          })}
          confirmLabel={t(confirm.kind === "exempt" ? "exempt_confirm" : "back_general_confirm")}
          confirmDisabled={mutations.isSubmitting}
          cancelLabel={t("button_cancel")}
          onConfirm={() => void handleConfirm()}
          onCancel={() => setConfirm(null)}
        >
          {confirm.kind === "back" && rowsCoveredBy(confirm.summary, confirm.own).length > 1 && (
            <ColocaAlert
              variant="warning"
              text={t("back_general_affects", {
                rows: rowsCoveredBy(confirm.summary, confirm.own)
                  .map((group) => scopeLabel(group, t))
                  .join(", "),
              })}
            />
          )}
          {mutations.error && <ColocaAlert variant="destructive" text={mutations.error} />}
        </ColocaAlertDialog>
      )}
    </>
  );
}
